import React from 'react';
import { Award, Shield, DollarSign, Truck, Heart, Zap, ThumbsUp, Target, Wrench, Gift, Percent, BadgeCheck, Car, CheckCircle, Sparkles, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';

const ICON_MAP = {
  Award, Shield, DollarSign, Truck, Heart, Zap, ThumbsUp, Target, Wrench, Gift, Percent, BadgeCheck, Car, CheckCircle, Sparkles, Star,
  // Legacy lowercase keys
  user: Award, car: Car, truck: Truck, shield: Shield
};

const defaultFeatures = [
  { icon: 'Award', title: 'One-Person Buying', desc: 'Skip the runaround. No sales manager shuffle. Our team handles sales, financing, and paperwork start to finish.' },
  { icon: 'Shield', title: 'Trade-Ins, Financing & Consignment', desc: 'Fair trade-in values, competitive financing, and consignment options for higher-value vehicles.' },
  { icon: 'Truck', title: 'Delivery Available', desc: 'We deliver across Canada. Your new vehicle can come right to your door.' },
  { icon: 'CheckCircle', title: 'Quality You Can Trust', desc: 'Every vehicle is thoroughly inspected and comes with a clean title guarantee.' },
];

const WhyChooseUs = () => {
  const { settings } = useSiteSettings();
  const whyImage = settings.whyImage || "https://images.unsplash.com/photo-1574023240744-64c47c8c0676?w=800&q=80";

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-8 bg-red-600"></span>
            <span className="text-red-600 font-semibold text-xs uppercase tracking-[0.22em]">
              {settings.whyTagline || "Why Choose Us"}
            </span>
            <span className="h-px w-8 bg-red-600"></span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 tracking-tight mb-4">
            {settings.whyTitle || "Why Choose Big Chief Motors?"}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            {settings.whySubtitle || "With years of experience in the industry, we've built our reputation on transparency, quality, and exceptional customer service."}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Caption sits under the image so it never covers it (the default image has text on a sign) */}
          <div className="rounded-2xl overflow-hidden shadow-lg bg-zinc-950">
            <img src={whyImage} alt="Big Chief Motors Dealership" className="w-full aspect-[3/2] object-cover bg-white" />
            <div className="p-5 sm:p-6 text-white">
              <p className="text-lg font-medium mb-2">
                {settings.whyImageText || "At Big Chief Motors, we make buying a vehicle simple, transparent, and stress-free."}
              </p>
              <Link to="/about">
                <Button variant="link" className="text-white p-0 hover:text-red-400 flex items-center gap-1 group">
                  {settings.whyImageLink || "Learn more about us"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => {
              const def = defaultFeatures[i - 1];
              const iconName = settings[`feature${i}Icon`] || def.icon;
              const IconComponent = ICON_MAP[iconName] || Award;
              return (
                <div key={i} className="p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 border border-transparent hover:border-gray-100 group flex flex-col h-full">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors flex-shrink-0">
                    <IconComponent size={24} className="text-red-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-black mb-2 min-h-[3.5rem] flex items-start">{settings[`feature${i}Title`] || def.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed flex-grow">{settings[`feature${i}Desc`] || def.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
